import { defineStore } from 'pinia'
import { ref, Ref } from 'vue'

type BaseProps = {
  message: string
}

type ErrorNotificationProps = BaseProps & {
  type: 'error'
  dismissable?: never
  icon?: never
}

type SuccessNotificationProps = BaseProps & {
  type: 'success'
  dismissable: boolean
  icon?: string
}

type InfoNotificationProps = BaseProps & {
  type: 'info'
  dismissable: boolean
  icon?: string
}

export type Notification =
  ErrorNotificationProps | InfoNotificationProps | SuccessNotificationProps

export interface AddNotificationType {
  ({ type, message }: Notification): void
}

type NotificationWithId = Notification & {
  id: number
}

interface NotificationsStore {
  notifications: Ref<NotificationWithId[]>
  addNotification: (notification: Notification) => void
  dismissNotification: (index: number) => void
}

const SELF_DESTRUCT_TIME = 5000
const MAXIMUM_LENGTH = 5

export const useNotificationStore = defineStore(
  'notifications',
  (): NotificationsStore => {
    let notificationIndex = 0
    const notifications = ref<NotificationWithId[]>([])

    function internalSelfDestruct(notification: NotificationWithId): void {
      const index = notifications.value.findIndex(
        (n) => n.id === notification.id
      )
      if (index !== -1) {
        dismissNotification(index)
      }
    }

    function addNotification(notification: Notification): void {
      const notificationWithId = { ...notification, id: notificationIndex++ }
      notifications.value.push(notificationWithId)
      if (notifications.value.length > MAXIMUM_LENGTH) {
        dismissNotification(0)
      }
      setTimeout(
        () => internalSelfDestruct(notificationWithId),
        SELF_DESTRUCT_TIME
      )
    }

    function dismissNotification(index: number): void {
      notifications.value.splice(index, 1)
    }

    return {
      notifications,
      addNotification,
      dismissNotification
    }
  }
)
